import { getVisitorId } from '../utils/visitorTracker';

import axiosInstance from './axiosInstance';
import { savePostAuthRedirect } from './postAuthRedirect';

const INTENT_KEY = 'campaign_coupon_intent';
const PENDING_AUTH_KEY = 'campaign_coupon_auth_pending';
const INTENT_TTL_MS = 24 * 60 * 60 * 1000;
const INTENT_CHANGED = 'campaign-intent-changed';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export interface CampaignIntent {
  code: string;
  event_id: string;
  visitor_id: string;
  returnPath: string;
  capturedAt: number;
  utm: Partial<Record<typeof UTM_KEYS[number], string>>;
}

export interface CampaignDetails {
  code: string;
  campaign_name: string;
  credits: number;
  audience_mode: string;
  allow_new_users: boolean;
  eligible_users?: 'new' | 'existing' | 'all';
  coupon_type?: 'signup' | 'campaign';
  is_active: boolean;
  eligible: boolean | null;
  already_claimed: boolean;
  message: string;
}

export interface CampaignClaim {
  status: 'claimed' | 'already_claimed';
  credits_awarded: number;
  balance: number;
}

export const campaignIntentSnapshot = (): string | null => {
  try {
    return localStorage.getItem(INTENT_KEY);
  } catch {
    return null;
  }
};

export const parseCampaignIntent = (raw: string | null): CampaignIntent | null => {
  try {
    const value = raw ? JSON.parse(raw) as CampaignIntent : null;

    if (!value?.code || !value.event_id || !value.visitor_id || typeof value.capturedAt !== 'number'
      || !value.returnPath?.startsWith('/?') || Date.now() - value.capturedAt > INTENT_TTL_MS) return null;

    return value;
  } catch {
    return null;
  }
};

export const readCampaignIntent = (): CampaignIntent | null => parseCampaignIntent(campaignIntentSnapshot());

export const subscribeCampaignIntent = (listener: () => void): (() => void) => {
  window.addEventListener(INTENT_CHANGED, listener);
  window.addEventListener('storage', listener);

  return () => {
    window.removeEventListener(INTENT_CHANGED, listener);
    window.removeEventListener('storage', listener);
  };
};

const storeCampaignIntent = (intent: CampaignIntent | null): CampaignIntent | null => {
  try {
    if (intent) localStorage.setItem(INTENT_KEY, JSON.stringify(intent));
    else localStorage.removeItem(INTENT_KEY);
    if (intent) savePostAuthRedirect('/');
    window.dispatchEvent(new Event(INTENT_CHANGED));
  } catch { /* Storage may be disabled; the offer never affects authentication. */ }

  return intent;
};

export const clearCampaignIntent = (): void => {
  storeCampaignIntent(null);
  try { localStorage.removeItem(PENDING_AUTH_KEY); } catch { /* Storage is optional. */ }
};

const resumeCampaignIntent = (freshPage: boolean): CampaignIntent | null => {
  if (freshPage) {
    try { localStorage.removeItem(PENDING_AUTH_KEY); } catch { /* Storage is optional. */ }
  }

  return readCampaignIntent();
};

export const prepareCampaignLogin = (): void => {
  const intent = readCampaignIntent();

  try {
    if (intent) {
      localStorage.setItem(PENDING_AUTH_KEY, intent.event_id);
      if (['/', '/login', '/signup'].includes(window.location.pathname)) savePostAuthRedirect('/');
    }
  } catch { /* Campaign tracking must not prevent sign-in. */ }
};

const newEventId = (): string => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const newIntent = (code: string, search: string, params: URLSearchParams): CampaignIntent => ({
  code, returnPath: `/${search}`, capturedAt: Date.now(), visitor_id: getVisitorId(),
  event_id: newEventId(),
  utm: Object.fromEntries(UTM_KEYS.filter(key => params.has(key)).map(key => [key, (params.get(key) || '').slice(0, 255)])),
});

const resolveIntent = (code: string, search: string, params: URLSearchParams, freshPage: boolean): CampaignIntent => {
  const saved = readCampaignIntent();
  let pendingAuth: string | null = null;

  try {
    pendingAuth = localStorage.getItem(PENDING_AUTH_KEY);
    if (freshPage) localStorage.removeItem(PENDING_AUTH_KEY);
  } catch { /* The stored offer is optional. */ }

  return saved?.code === code && saved.returnPath === `/${search}` && (!freshPage || saved.event_id === pendingAuth)
    ? saved : newIntent(code, search, params);
};

export const captureCampaignIntent = (pathname: string, search: string, freshPage = false): CampaignIntent | null => {
  if (!['/', '/login', '/signup'].includes(pathname)) return readCampaignIntent();
  const params = new URLSearchParams(search);
  const code = params.get('coupon')?.trim().toUpperCase();

  if (!code || !/^[A-Z0-9][A-Z0-9_-]{3,31}$/.test(code)) return resumeCampaignIntent(freshPage);

  return storeCampaignIntent(resolveIntent(code, search, params, freshPage));
};

export const campaignCouponService = {
  details: async (code: string): Promise<CampaignDetails> => (
    (await axiosInstance.get(`/api/v1/coupons/${encodeURIComponent(code)}`)).data
  ),
  claim: async (code: string): Promise<CampaignClaim> => (
    (await axiosInstance.post(`/api/v1/coupons/${encodeURIComponent(code)}/claim`)).data
  ),
  arrival: async (intent: CampaignIntent): Promise<void> => {
    await axiosInstance.post(`/api/v1/coupons/${encodeURIComponent(intent.code)}/arrivals`, {
      event_id: intent.event_id, visitor_id: intent.visitor_id, ...intent.utm,
    });
  },
};
